import { useEffect } from 'react';
import * as Sentry from '@sentry/react-router';
import { logToConsole } from '~/logging/consoleLogger';
import type { ICustomerTypeData } from '~/dtos/ProdegaApiWebshop-api-typings-generated';
import type { ClientTrackingConfig, DeploymentInfo } from '~/config/deployment-config';
import { getTransgourmetTraceIdClientSide } from '~/shell/cookies';
import type { ContextLoginInfo } from '~/shell/globalContext.ts';

let LOGIN_INFO: ContextLoginInfo | undefined;
let TRACKING_CONFIG: ClientTrackingConfig | undefined;
let DEPLOYMENT_INFO: DeploymentInfo | undefined;

export function useLoggerLoginInfo(
  loginInfo: ContextLoginInfo | undefined,
  trackingConfig: ClientTrackingConfig | undefined,
  deploymentInfo: DeploymentInfo | undefined
) {
  useEffect(() => {
    LOGIN_INFO = loginInfo;
    TRACKING_CONFIG = trackingConfig;
    DEPLOYMENT_INFO = deploymentInfo;
  }, [loginInfo, trackingConfig, deploymentInfo]);
}

export function enableConsoleLoggingForTesting() {
  DEPLOYMENT_INFO = { DEPLOYMENT_ENVIRONMENT: 'DEVELOPMENT', ARTIFACT_CREATED: '', ARTIFACT_REVISION: '', ARTIFACT_VERSION: '' };
}

export function captureError(message: string, contextInfo?: any) {
  let userInfo: any = undefined;
  if (LOGIN_INFO) {
    userInfo = {
      customerNumber: LOGIN_INFO.customerNumber,
      customerCategory: LOGIN_INFO.customerCategory,
      customerType: LOGIN_INFO.customerType as ICustomerTypeData,
      customerName: LOGIN_INFO.customerName,
      companyName: LOGIN_INFO.companyName,
      email: LOGIN_INFO.email
    };
  }
  const transgourmetTraceId = getTransgourmetTraceIdClientSide();
  if (transgourmetTraceId) {
    userInfo = userInfo || {};
    userInfo.traceId = transgourmetTraceId;
  }

  logToConsole({ loggerName: 'error-tracker', logLevel: 'ERROR', message, args: [contextInfo, userInfo] });

  Sentry.withScope(scope => {
    if (userInfo) {
      scope.setUser(userInfo);
    }
    if (contextInfo) {
      scope.setContext('info', contextInfo);
    }

    let error: Error;
    if (contextInfo?.error && contextInfo.error instanceof Error) {
      error = new Error(message, { cause: contextInfo.error });
    } else {
      error = new Error(message);
    }
    Sentry.captureException(error);
  });
}

export function isDebugLogEnabled() {
  let debugLogEnabled = false;
  if (
    typeof window !== 'undefined' &&
    window.sessionStorage?.getItem('tgs_enableDebugLog') === 'true'
  ) {
    debugLogEnabled = true;
  } else if (DEPLOYMENT_INFO?.DEPLOYMENT_ENVIRONMENT === 'DEVELOPMENT') {
    debugLogEnabled = true;
  } else if (TRACKING_CONFIG) {
    debugLogEnabled = TRACKING_CONFIG.enableDebugLog;
  }
  return debugLogEnabled;
}

export function isTrackingLogEnabled() {
  let trackingLogEnabled = false;
  if (
    typeof window !== 'undefined' &&
    window.sessionStorage?.getItem('tgs_enableTrackingLog') === 'true'
  ) {
    trackingLogEnabled = true;
  } else if (TRACKING_CONFIG) {
    trackingLogEnabled = TRACKING_CONFIG.enableTrackingLog;
  }
  return trackingLogEnabled;
}
